/**
 * Client helpers for form submission lifecycle (validation, read-only rules).
 */

import { isFormFileAnswerEmpty } from './formFileUploads';
import { extractUserAnswers as extractSubmissionUserAnswers } from './submissionAnswers';

export const SUBMISSION_STATUS = Object.freeze({
  DRAFT: 'draft',
  SUBMITTED: 'submitted'
});

export const GRADING_STATUS = Object.freeze({
  NOT_REQUIRED: 'not_required',
  PENDING: 'pending',
  COMPLETE: 'complete'
});

export const SUBMISSION_ACTIONS = Object.freeze({
  SAVE: 'save',
  SUBMIT: 'submit',
  GRADE: 'grade'
});

export const SUBMISSION_STATUS_LABELS = {
  [SUBMISSION_STATUS.DRAFT]: 'Draft',
  [SUBMISSION_STATUS.SUBMITTED]: 'Submitted'
};

export const GRADING_STATUS_LABELS = {
  [GRADING_STATUS.NOT_REQUIRED]: 'No grading',
  [GRADING_STATUS.PENDING]: 'Awaiting review',
  [GRADING_STATUS.COMPLETE]: 'Graded'
};

/** User-facing grading badge in the assignment header. */
export function resolveGradingBadgeLabel(submission) {
  if (!submission?.grading_status) return null;

  const isDraft =
    submission.status === SUBMISSION_STATUS.DRAFT || submission.status == null;

  if (submission.grading_status === GRADING_STATUS.NOT_REQUIRED) {
    if (isDraft) return 'Click Submit to turn in your work.';
    return null;
  }

  return GRADING_STATUS_LABELS[submission.grading_status] || null;
}

function isEmptyAnswer(value) {
  if (value == null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === 'object' && value.name) return false;
  return false;
}

export function extractUserAnswers(answers) {
  return extractSubmissionUserAnswers(answers);
}

export function isQuestionAnswered(question, answers, pendingBlobs = {}) {
  const qid = question?.question_id;
  if (!qid) return true;
  const value = answers?.[qid];
  if (question.type === 'file-upload') {
    if (!isFormFileAnswerEmpty(value, question.allowMultiple !== false)) return true;
    const pending = pendingBlobs[qid];
    if (pending && (Array.isArray(pending) ? pending.length : 1)) return true;
    return false;
  }
  return !isEmptyAnswer(value);
}

export function validateRequiredForSubmit(form, answers, pendingBlobs = {}) {
  const questions = form?.questions || [];
  const missing = [];
  for (const q of questions) {
    if (!q?.required || !q?.question_id) continue;
    if (!isQuestionAnswered(q, answers, pendingBlobs)) {
      missing.push(q.question_id);
    }
  }
  if (missing.length === 0) return { ok: true };
  return {
    ok: false,
    missing,
    message: 'Complete all required questions before submitting.'
  };
}

export function canSubmitDraft(submission) {
  return !submission || submission.status === SUBMISSION_STATUS.DRAFT || submission.status == null;
}

export function isSubmissionLockedForStudent(submission) {
  return submission?.status === SUBMISSION_STATUS.SUBMITTED;
}

function parseUpdateHistory(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Derived: first history entry with status=submitted, else created_at when never draft. */
export function resolveSubmittedAt(submission) {
  if (!submission || submission.status !== SUBMISSION_STATUS.SUBMITTED) return null;

  const history = parseUpdateHistory(submission.update_history);
  const submitEntry = history.find(
    (entry) => entry?.action === 'submit' || entry?.status === SUBMISSION_STATUS.SUBMITTED
  );
  if (submitEntry?.at) return submitEntry.at;

  return submission.created_at || null;
}

export function getSubmissionStatusLabel(submission) {
  return submission?.status === SUBMISSION_STATUS.SUBMITTED
    ? SUBMISSION_STATUS_LABELS[SUBMISSION_STATUS.SUBMITTED]
    : SUBMISSION_STATUS_LABELS[SUBMISSION_STATUS.DRAFT];
}

/** List is newest-first; attempt 1 is the oldest row. */
export function resolveSubmissionAttemptNumber(index, total) {
  return Math.max(1, total - index);
}

export function resolveSubmissionAttemptDisplay(submission, index, total) {
  const submittedAt = resolveSubmittedAt(submission);
  const attemptNumber = total > 1 ? resolveSubmissionAttemptNumber(index, total) : null;
  const when = submittedAt
    ? new Date(submittedAt).toLocaleString()
    : submission?.created_at
      ? new Date(submission.created_at).toLocaleString()
      : attemptNumber != null
        ? `Attempt ${attemptNumber}`
        : null;
  const score = submission?.answers?.score ?? submission?.answers?.points;
  const max = submission?.answers?.maxScore;
  const scoreLabel = score != null && max != null ? `${score}/${max} pt` : null;
  const statusKey =
    submission?.status === SUBMISSION_STATUS.SUBMITTED
      ? SUBMISSION_STATUS.SUBMITTED
      : SUBMISSION_STATUS.DRAFT;

  return {
    attemptNumber,
    when,
    scoreLabel,
    statusKey,
    statusLabel: SUBMISSION_STATUS_LABELS[statusKey]
  };
}

export function formatSubmissionListLabel(submission, index) {
  const submittedAt = resolveSubmittedAt(submission);
  const when = submittedAt
    ? new Date(submittedAt).toLocaleString()
    : submission?.created_at
      ? new Date(submission.created_at).toLocaleString()
      : `Attempt ${index + 1}`;
  const status =
    submission?.status === SUBMISSION_STATUS.SUBMITTED
      ? SUBMISSION_STATUS_LABELS[SUBMISSION_STATUS.SUBMITTED]
      : SUBMISSION_STATUS_LABELS[SUBMISSION_STATUS.DRAFT];
  const score = submission?.answers?.score ?? submission?.answers?.points;
  const max = submission?.answers?.maxScore;
  const parts = [when, status];
  if (score != null && max != null) parts.push(`${score}/${max} pt`);
  return parts.join(' · ');
}
